// src/userFlow/SignInPage.js
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import API_BASE from '../apiBase';
import QaAlert from '../components/QaAlert';
import RequiredMark from '../components/RequiredMark';

export default function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // prefill from signup redirect
  useEffect(() => {
    if (location.state?.email) setEmail(location.state.email);
  }, [location.state]);

  async function onSubmit(e) {
    e.preventDefault();
    setServerError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setServerError(data.error || 'Login failed');
        return;
      }

    
     
      const dest =
        ['admin','staff'].includes(data.role)
          ? (location.state?.nextAdmin || '/admin')
          : (location.state?.next || '/');


      navigate(dest, { replace: true });
    } catch {
      setServerError('Network error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between" data-qa="signin-page">
      <div className="flex-grow flex items-center justify-center px-4">
        <div className="bg-white shadow-lg rounded-xl w-full max-w-md p-8" data-qa="signin-card">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent text-center">
            Sign In
          </h2>
          <p className="text-gray-600 text-center mt-2 mb-4">
            Welcome back! Please sign in to your account.
          </p>

          {/* Always-mounted alert region (stable selector) */}
          <QaAlert id="signin-alert" dataQa="qa-signin-alert" message={serverError} variant="error" />

          <form className="space-y-5" onSubmit={onSubmit} data-qa="qa-signin-form">
            <div data-qa="qa-signin-email-field">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="signin-email">
                Email Address <RequiredMark />
              </label>
              <input
                id="signin-email"
                data-qa="qa-signin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                aria-describedby="signin-error-email"
                aria-invalid="false"
                required
                aria-required="true"
              />
              <p id="signin-error-email" data-qa="qa-signin-email-error" className="sr-only"></p>
            </div>

            <div data-qa="qa-signin-password-field">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="signin-password">
                Password <RequiredMark />
              </label>
              <input
                id="signin-password"
                data-qa="qa-signin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                aria-describedby="signin-error-password"
                aria-invalid="false"
                required
                aria-required="true"
              />
              <p id="signin-error-password" data-qa="qa-signin-password-error" className="sr-only"></p>
            </div>

            <button
              type="submit"
              data-qa="qa-signin-submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6" data-qa="signin-links">
            Don’t have an account?{' '}
            <Link to="/signup" className="text-orange-500 hover:underline" data-qa="qa-signin-to-signup">
              Sign Up
            </Link>
          </p>
          <p className="text-center text-xs text-gray-400 mt-2">
            <Link to="/" className="hover:underline">← Back to Home</Link>
          </p>
        </div>
      </div>

      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="text-center text-sm text-gray-500">
          <Link to="/about" className="hover:underline mr-4">About Us</Link>
          © 2025 SodaSips. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
